import { forwardRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Card — a bounded content block on the canvas.
 *
 * Cards carry the hairline and the elevation; the things inside them carry the colour.
 * `interactive` adds the pointer affordances for a card that is itself a link or button —
 * it does not make the element focusable, so pass `asChild`-style markup or an <a>.
 */

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardTone = 'raised' | 'inset' | 'flat';

const PADDING: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-5',
};

const TONE: Record<CardTone, string> = {
  raised: 'bg-[var(--surface-1)] border-line shadow-[var(--shadow-1)]',
  inset: 'bg-[var(--surface-inset)] border-line-subtle',
  flat: 'bg-[var(--surface-1)] border-line-subtle',
};

export interface CardProps extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  /** Header title. Omit and compose <CardHeader> yourself for anything richer. */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Trailing header slot. */
  actions?: ReactNode;
  footer?: ReactNode;
  padding?: CardPadding;
  tone?: CardTone;
  interactive?: boolean;
  /** Renders as another element (`'a'`, `'button'`, `'li'`…). */
  as?: ElementType;
  children?: ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    title,
    subtitle,
    actions,
    footer,
    padding = 'md',
    tone = 'raised',
    interactive = false,
    as,
    className,
    children,
    ...rest
  },
  ref,
) {
  const Component = (as ?? 'div') as ElementType;
  const hasHeader = Boolean(title || subtitle || actions);

  return (
    <Component
      ref={ref}
      className={cn(
        'flex min-w-0 flex-col overflow-hidden rounded-[var(--radius-lg)] border',
        TONE[tone],
        interactive &&
          cn(
            'cursor-pointer transition-[border-color,box-shadow,background-color] ease-[var(--ease-out)] duration-[var(--dur-fast)]',
            'hover:border-line-strong hover:shadow-[var(--shadow-2)]',
            'active:bg-[var(--surface-hover)]',
            'focus-visible:outline-2 focus-visible:outline-offset-1 outline-[color:var(--focus)]',
          ),
        className,
      )}
      {...rest}
    >
      {hasHeader ? (
        <CardHeader>
          <div className="min-w-0 flex-1">
            {title ? <CardTitle>{title}</CardTitle> : null}
            {subtitle ? (
              <p className="mt-1 truncate text-sm leading-snug text-muted">{subtitle}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-1">{actions}</div> : null}
        </CardHeader>
      ) : null}

      <div
        className={cn(
          'min-w-0 flex-1',
          PADDING[padding],
          hasHeader && padding !== 'none' && 'pt-3',
        )}
      >
        {children}
      </div>

      {footer ? (
        <div className="border-t border-line-subtle bg-[var(--surface-2)] px-4 py-3">{footer}</div>
      ) : null}
    </Component>
  );
});

export const CardHeader = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function CardHeader({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex items-start gap-3 px-4 pt-4 pb-0', className)}
        {...rest}
      />
    );
  },
);

export const CardTitle = forwardRef<HTMLHeadingElement, ComponentPropsWithoutRef<'h3'>>(
  function CardTitle({ className, ...rest }, ref) {
    return (
      <h3
        ref={ref}
        className={cn(
          'truncate text-md font-semibold tracking-[var(--tracking-tight)] text-primary',
          className,
        )}
        {...rest}
      />
    );
  },
);

export const CardBody = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function CardBody({ className, ...rest }, ref) {
    return <div ref={ref} className={cn('min-w-0 p-4', className)} {...rest} />;
  },
);

export const CardFooter = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-2 border-t border-line-subtle bg-[var(--surface-2)] px-4 py-3',
          className,
        )}
        {...rest}
      />
    );
  },
);

/** Hairline rule that stops at the card's padding, for sectioned cards. */
export const CardDivider = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(
  function CardDivider({ className, ...rest }, ref) {
    return <div ref={ref} role="separator" className={cn('h-px bg-[var(--line-subtle)]', className)} {...rest} />;
  },
);
